import {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
  useRef,
  type ReactNode,
  useMemo,
} from "react";
import type { RealtimeChannel } from "@lyder-talk/core/lib/realtime-client";
import { auth } from "@lyder-talk/core/lib/auth-client";
import { realtime } from "@lyder-talk/core/lib/realtime-client";
import { apiPost } from "@/lib/api-client";

const STUDYING_NOW_CHANNEL = "studying-now";

export interface StudyingPresence {
  userId: string;
  email?: string;
  displayName?: string | null;
  avatarUrl?: string | null;
  topicTitle?: string | null;
  sessionSlug?: string | null;
  /** Cover art for the topic being practised, so presence can show the room. */
  bannerUrl?: string | null;
  /** Topic category, for the cover fallback when the session has no banner. */
  category?: string | null;
  startedAt: string;
}

interface StudyingNowContextValue {
  userId: string | null;
  isSubscribed: boolean;
  presenceState: Map<string, StudyingPresence>;
  startStudying: (
    data: Omit<StudyingPresence, "userId" | "startedAt">
  ) => Promise<void>;
  stopStudying: () => Promise<void>;
}

const StudyingNowContext = createContext<StudyingNowContextValue | null>(null);

interface StudyingNowProviderProps {
  children: ReactNode;
}

export const StudyingNowProvider = ({ children }: StudyingNowProviderProps) => {
  const [userId, setUserId] = useState<string | null>(null);
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [presenceState, setPresenceState] = useState<
    Map<string, StudyingPresence>
  >(new Map());

  const channelRef = useRef<RealtimeChannel | null>(null);
  const isTrackingRef = useRef(false);
  const currentPresenceRef = useRef<Partial<StudyingPresence>>({});
  const presenceUpdateTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Resolve the authenticated user. "Studying now" is auth-gated — anonymous
  // visitors can observe the channel but never appear in it.
  useEffect(() => {
    const getCurrentUser = async () => {
      const {
        data: { session },
      } = await auth.getSession();
      setUserId(session?.user?.id ?? null);
    };
    getCurrentUser();

    const { data: authListener } = auth.onAuthStateChange(
      (_event, session) => {
        setUserId(session?.user?.id ?? null);
      }
    );

    return () => {
      authListener.subscription.unsubscribe();
    };
  }, []);

  // Subscribe to the global channel so every client can *read* who is studying.
  // We only call track() later, via startStudying(), for users who are actually
  // in a practice session.
  useEffect(() => {
    const newChannel = realtime.channel(STUDYING_NOW_CHANNEL);

    // Rebuild presence on sync, debounced to avoid re-render / RPC storms.
    newChannel.on("presence", { event: "sync" }, () => {
      if (presenceUpdateTimeoutRef.current) {
        clearTimeout(presenceUpdateTimeoutRef.current);
      }

      presenceUpdateTimeoutRef.current = setTimeout(async () => {
        const state = newChannel.presenceState<StudyingPresence>();
        const newPresenceState = new Map<string, StudyingPresence>();

        const usersNeedingAvatars: string[] = [];

        Object.values(state).forEach((presences) => {
          const presence = presences[presences.length - 1];
          if (presence && presence.userId) {
            newPresenceState.set(presence.userId, {
              userId: presence.userId,
              email: presence.email,
              displayName: presence.displayName,
              avatarUrl: presence.avatarUrl,
              topicTitle: presence.topicTitle,
              sessionSlug: presence.sessionSlug,
              // The cover has to survive this rebuild: it is what the Home
              // band renders, and dropping it here silently sent every room
              // back to a text-only card.
              bannerUrl: presence.bannerUrl ?? null,
              category: presence.category ?? null,
              startedAt: presence.startedAt || new Date().toISOString(),
            });

            if (!presence.avatarUrl) {
              usersNeedingAvatars.push(presence.userId);
            }
          }
        });

        if (usersNeedingAvatars.length > 0) {
          try {
            const userData = (await apiPost<{ users: unknown[] }>("/v1/users/batch", {
              ids: usersNeedingAvatars,
            })).users;
            const userError = null;

            if (userError) {
              console.error("Error fetching batch user data:", userError);
            } else if (userData) {
              userData.forEach(
                (item: {
                  user_id: string;
                  avatar_url: string | null;
                  display_name: string | null;
                }) => {
                  const user = newPresenceState.get(item.user_id);
                  if (user) {
                    user.avatarUrl = item.avatar_url;
                    user.displayName = item.display_name;
                  }
                }
              );
            }
          } catch (err) {
            console.error("Error fetching user data in batch:", err);
          }
        }

        setPresenceState(newPresenceState);
      }, 500); // Debounce for 500ms
    });

    newChannel.subscribe((status) => {
      if (status === "SUBSCRIBED") {
        setIsSubscribed(true);
      }
    });

    channelRef.current = newChannel;

    return () => {
      if (presenceUpdateTimeoutRef.current) {
        clearTimeout(presenceUpdateTimeoutRef.current);
      }
      newChannel.untrack();
      realtime.removeChannel(newChannel);
      channelRef.current = null;
      isTrackingRef.current = false;
      setIsSubscribed(false);
    };
  }, []);

  const startStudying = useCallback(
    async (data: Omit<StudyingPresence, "userId" | "startedAt">) => {
      const channel = channelRef.current;
      if (!channel || !userId || !isSubscribed) return;

      // No-op only when the whole payload is unchanged — avoids re-track()
      // storms when parent components re-render without a real session change,
      // while still letting the cover arrive after the session metadata loads
      // (topic and slug are set first, banner and category a beat later).
      const prev = currentPresenceRef.current;
      if (
        isTrackingRef.current &&
        prev.userId === userId &&
        prev.topicTitle === data.topicTitle &&
        prev.sessionSlug === data.sessionSlug &&
        prev.bannerUrl === data.bannerUrl &&
        prev.category === data.category
      ) {
        return;
      }

      currentPresenceRef.current = {
        ...currentPresenceRef.current,
        ...data,
        userId,
        startedAt:
          currentPresenceRef.current.startedAt ?? new Date().toISOString(),
      };

      isTrackingRef.current = true;
      await channel.track(currentPresenceRef.current as StudyingPresence, {
        key: userId,
      });
    },
    [userId, isSubscribed]
  );

  const stopStudying = useCallback(async () => {
    const channel = channelRef.current;
    if (!channel || !isTrackingRef.current) return;

    isTrackingRef.current = false;
    currentPresenceRef.current = {};
    await channel.untrack();
  }, []);

  const value = useMemo(
    () => ({
      userId,
      isSubscribed,
      presenceState,
      startStudying,
      stopStudying,
    }),
    [userId, isSubscribed, presenceState, startStudying, stopStudying]
  );

  return (
    <StudyingNowContext.Provider value={value}>
      {children}
    </StudyingNowContext.Provider>
  );
};

export const useStudyingNow = (): StudyingNowContextValue => {
  const context = useContext(StudyingNowContext);
  if (!context) {
    // Safe default for components rendered outside the provider.
    return {
      userId: null,
      isSubscribed: false,
      presenceState: new Map(),
      startStudying: async () => {},
      stopStudying: async () => {},
    };
  }
  return context;
};
